<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ZapiWootAPI from 'dashboard/api/zapiwoot';

const isLoading = ref(false);
const isSaving = ref(false);

const data = reactive({
  channels: [],
  agents: [],
  routeInboxes: [],
  settings: [],
});

const form = reactive({
  routing_channel_id: '',
  agent_id: '',
  agent_display_name: '',
  inbox_id: '',
  is_active: true,
  direct_contact_enabled: false,
  show_in_agent_menu: false,
  agent_public_identifier: '',
  agent_menu_order: 100,
  sync_inbox: true,
  sync_team: true,
});

const selectedInbox = computed(() =>
  data.routeInboxes.find(inbox => Number(inbox.id) === Number(form.inbox_id))
);

const selectedSetting = computed(() =>
  data.settings.find(
    setting =>
      Number(setting.routing_channel_id) === Number(form.routing_channel_id) &&
      Number(setting.agent_id) === Number(form.agent_id)
  )
);

const applyAgentSettingToForm = setting => {
  form.agent_display_name = setting.agent_display_name || '';
  form.inbox_id = setting.inbox_id || '';
  form.is_active = Number(setting.is_active) === 1;
  form.direct_contact_enabled = Number(setting.direct_contact_enabled) === 1;
  form.show_in_agent_menu = Number(setting.show_in_agent_menu) === 1;
  form.agent_public_identifier = setting.agent_public_identifier || '';
  form.agent_menu_order = Number(setting.agent_menu_order || 100);
};

const resetForm = () => {
  form.agent_id = '';
  form.agent_display_name = '';
  form.inbox_id = '';
  form.is_active = true;
  form.direct_contact_enabled = false;
  form.show_in_agent_menu = false;
  form.agent_public_identifier = '';
  form.agent_menu_order = 100;
  form.sync_inbox = true;
  form.sync_team = true;
};

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const response = await ZapiWootAPI.get();
    const payload = response.data || {};

    if (payload.error) {
      useAlert(payload.error);
      return;
    }

    data.channels = payload.channels || [];
    data.agents = payload.agents || [];
    data.routeInboxes = payload.route_inboxes || [];
    data.settings = payload.settings || [];

    if (!form.routing_channel_id && data.channels.length) {
      form.routing_channel_id = data.channels[0].id;
    }
  } catch (error) {
    useAlert('Erro ao carregar configuracoes WhatsApp dos agentes.');
  } finally {
    isLoading.value = false;
  }
};

const onAgentChange = () => {
  const existing = selectedSetting.value;
  if (!existing) {
    form.agent_display_name = '';
    form.inbox_id = '';
    form.is_active = true;
    form.direct_contact_enabled = false;
    form.show_in_agent_menu = false;
    form.agent_public_identifier = '';
    form.agent_menu_order = 100;
    return;
  }

  applyAgentSettingToForm(existing);
};

const editSetting = setting => {
  form.routing_channel_id = setting.routing_channel_id || form.routing_channel_id;
  form.agent_id = setting.agent_id || '';
  applyAgentSettingToForm(setting);
  form.sync_inbox = true;
  form.sync_team = true;
};

const saveSettings = async () => {
  if (!form.routing_channel_id || !form.agent_id || !form.inbox_id) {
    useAlert('Selecione canal, agente e setor.');
    return;
  }

  isSaving.value = true;
  try {
    const response = await ZapiWootAPI.create({
      routing_channel_id: form.routing_channel_id,
      agent_id: form.agent_id,
      agent_display_name: form.agent_display_name,
      inbox_id: form.inbox_id,
      is_active: form.is_active,
      direct_contact_enabled: form.direct_contact_enabled,
      show_in_agent_menu: form.show_in_agent_menu,
      agent_public_identifier: form.agent_public_identifier,
      agent_menu_order: form.agent_menu_order,
      sync_inbox: form.sync_inbox,
      sync_team: form.sync_team,
    });

    if (response.data?.error) {
      useAlert(response.data.error);
      return;
    }

    useAlert('Configuracao WhatsApp do agente salva com sucesso.');
    resetForm();
    await loadSettings();
  } catch (error) {
    useAlert('Erro ao salvar configuracao WhatsApp do agente.');
  } finally {
    isSaving.value = false;
  }
};

const agentLabel = agent => {
  const name = agent.name || agent.available_name || `Agent ${agent.id}`;
  return agent.email ? `${name} (${agent.email})` : name;
};

const channelLabel = channelId => {
  const channel = data.channels.find(
    item => Number(item.id) === Number(channelId)
  );

  if (!channel) return `#${channelId}`;

  return channel.phone ? `${channel.name} - ${channel.phone}` : channel.name;
};

onMounted(loadSettings);
</script>

<template>
  <section class="mb-6 grid gap-4 rounded-lg border border-n-weak bg-n-alpha-2 p-4">
    <div class="flex flex-col gap-1">
      <h2 class="text-lg font-semibold text-n-slate-12">
        WhatsApp direct agent selection
      </h2>
      <p class="text-sm text-n-slate-11">
        Configure public agent names, /AGENTES visibility, aliases, and sector assignment.
      </p>
    </div>

    <div
      v-if="isLoading"
      class="rounded-md border border-n-weak bg-n-background p-3 text-sm text-n-slate-11"
    >
      Loading WhatsApp agent settings...
    </div>

    <template v-else>
      <form class="grid gap-4" @submit.prevent="saveSettings">
        <div class="grid gap-4 md:grid-cols-3">
          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            WhatsApp number
            <select
              v-model="form.routing_channel_id"
              class="rounded-md border border-n-weak bg-n-background p-2"
              @change="resetForm"
            >
              <option
                v-for="channel in data.channels"
                :key="channel.id"
                :value="channel.id"
              >
                {{ channel.name }}{{ channel.phone ? ` - ${channel.phone}` : '' }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            Agent
            <select
              v-model="form.agent_id"
              class="rounded-md border border-n-weak bg-n-background p-2"
              @change="onAgentChange"
            >
              <option value="">Select...</option>
              <option
                v-for="agent in data.agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agentLabel(agent) }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            Public name
            <input
              v-model="form.agent_display_name"
              class="rounded-md border border-n-weak bg-n-background p-2"
              placeholder="Ex: Ge"
            />
          </label>
        </div>

        <div class="grid gap-4 md:grid-cols-3">
          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            Sector / routing inbox
            <select
              v-model="form.inbox_id"
              class="rounded-md border border-n-weak bg-n-background p-2"
            >
              <option value="">Select...</option>
              <option
                v-for="inbox in data.routeInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.department }} - {{ inbox.name }} (#{{ inbox.id }})
              </option>
            </select>
            <span
              v-if="selectedInbox"
              class="text-xs font-normal text-n-slate-10"
            >
              Department: {{ selectedInbox.department }}
            </span>
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            Public alias
            <input
              v-model="form.agent_public_identifier"
              class="rounded-md border border-n-weak bg-n-background p-2"
              placeholder="Ex: suporte-ge"
            />
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            /AGENTES order
            <input
              v-model.number="form.agent_menu_order"
              class="rounded-md border border-n-weak bg-n-background p-2"
              min="1"
              type="number"
            />
          </label>
        </div>

        <div class="flex flex-wrap gap-5 text-sm text-n-slate-12">
          <label class="flex items-center gap-2">
            <input v-model="form.is_active" type="checkbox" />
            Active
          </label>

          <label class="flex items-center gap-2">
            <input v-model="form.direct_contact_enabled" type="checkbox" />
            Direct contact
          </label>

          <label class="flex items-center gap-2">
            <input v-model="form.show_in_agent_menu" type="checkbox" />
            Show in /AGENTES
          </label>

          <label class="flex items-center gap-2">
            <input v-model="form.sync_inbox" type="checkbox" />
            Add to inbox
          </label>

          <label class="flex items-center gap-2">
            <input v-model="form.sync_team" type="checkbox" />
            Add to matching team
          </label>
        </div>

        <div class="flex flex-wrap gap-3">
          <button
            type="submit"
            class="rounded-md bg-n-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            :disabled="isSaving"
          >
            {{ isSaving ? 'Saving...' : 'Save WhatsApp agent settings' }}
          </button>

          <button
            type="button"
            class="rounded-md border border-n-weak px-4 py-2 text-sm font-semibold text-n-slate-12"
            @click="resetForm"
          >
            Clear
          </button>
        </div>
      </form>

      <div class="overflow-hidden rounded-lg border border-n-weak">
        <div class="border-b border-n-weak bg-n-alpha-2 px-4 py-3">
          <h3 class="font-semibold text-n-slate-12">
            Current WhatsApp agent settings
          </h3>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-n-alpha-2 text-left text-n-slate-11">
              <tr>
                <th class="p-3">Number</th>
                <th class="p-3">Agent</th>
                <th class="p-3">Public name</th>
                <th class="p-3">Department</th>
                <th class="p-3">Inbox</th>
                <th class="p-3">Team</th>
                <th class="p-3">Active</th>
                <th class="p-3">Direct</th>
                <th class="p-3">Menu</th>
                <th class="p-3">Alias</th>
                <th class="p-3">Order</th>
                <th class="p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!data.settings.length">
                <td class="p-3 text-n-slate-10" colspan="12">
                  No WhatsApp agent settings configured.
                </td>
              </tr>

              <tr
                v-for="setting in data.settings"
                :key="`${setting.routing_channel_id}-${setting.agent_id}-${setting.department}`"
                class="border-t border-n-weak"
              >
                <td class="p-3">{{ channelLabel(setting.routing_channel_id) }}</td>
                <td class="p-3">{{ setting.agent_name || setting.agent_id }}</td>
                <td class="p-3">{{ setting.agent_display_name }}</td>
                <td class="p-3">{{ setting.department }}</td>
                <td class="p-3">#{{ setting.inbox_id }}</td>
                <td class="p-3">{{ setting.team_id || '-' }}</td>
                <td class="p-3">{{ Number(setting.is_active) ? 'Yes' : 'No' }}</td>
                <td class="p-3">
                  {{ Number(setting.direct_contact_enabled) ? 'Yes' : 'No' }}
                </td>
                <td class="p-3">
                  {{ Number(setting.show_in_agent_menu) ? 'Yes' : 'No' }}
                </td>
                <td class="p-3">{{ setting.agent_public_identifier || '-' }}</td>
                <td class="p-3">{{ setting.agent_menu_order || 100 }}</td>
                <td class="p-3">
                  <button
                    type="button"
                    class="text-n-brand underline"
                    @click="editSetting(setting)"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>
